import { Button } from '@edsn/pie-components';
import AddIcon from '@iconify/icons-material-symbols/add';
import { differenceInMonths } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { useUserHasRole } from '#pie/auth/useUserHasRole';
import { CreateContactModal } from '#pie/components/contact-modal/CreateContactModal';
import { CreateTeamForm } from '#pie/components/create-team-form/CreateTeamForm';
import { Icon } from '#pie/components/icon/Icon';
import { Page, PageHeader } from '#pie/components/page/Page';
import { PageHead } from '#pie/components/page-head/PageHead';
import { Stack } from '#pie/components/stack/Stack';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '#pie/components/tabs';
import { UserManagementExport } from '#pie/components/user-management-export/UserManagementExport';
import { VerifyDataButton } from '#pie/components/verify-data-button/VerifyDataButton';
import {
  AuthorizationRoles,
  useGetCprManagementContacts,
} from '#pie/internal/api/pie-bff';
import { emptyFilterAtom } from '#pie/stores/searchOnlyFilter';
import { ContactGroupManagement } from './cpr/ContactGroupManagement';
import { ContactPersonManagement } from './cpr/ContactPersonManagement';
import { MarketPartyRegistration } from './market-party-registration/MarketPartyRegistration';
import { Notifications } from './notifications/Notifications';
import { PreferredPartyAdministration } from './preffered_party/PreferredPartyAdministration';
import { TeamManagement } from './teams/TeamManagement';
import { UserManagement } from './users/UserManagement';

export enum OrganisationTabs {
  MarketPartyRegistration = 'marktpartijregistratie',
  PreferredPartyRegistration = 'preferente-partijen-administratie',
  UserManagement = 'gebruikersbeheer',
  TeamManagement = 'teamsbeheer',
  Notifications = 'notificaties',
  ContactGroupManagement = 'contactgroepenbeheer',
  ContactPersonManagement = 'contactpersonenbeheer',
}

export const OrganisationManagement = () => {
  const { t } = useTranslation();
  const { t: tom } = useTranslation('organisationManagement');
  const { t: tmp } = useTranslation('marketParty');
  const { t: thl } = useTranslation('hyperlinks');
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useAtomValue(emptyFilterAtom);
  const { data, isLoading } = useGetCprManagementContacts({
    pagination: { pageIndex: 0, pageSize: 100 },
    filter: filters,
  });

  const tab = location.pathname.split('/').at(2) as OrganisationTabs;

  const hasCprAdminRole = useUserHasRole({ role: AuthorizationRoles.CprAdmin });
  const hasManagementRole = useUserHasRole({
    role: AuthorizationRoles.SuperUser,
  });
  const hasMarketPartyRole = useUserHasRole({
    role: AuthorizationRoles.MarketPartyRegistryRead,
  });

  const lastVerified = data?.latestContactValidationDate
    ? new Date(data.latestContactValidationDate)
    : null;

  const isVerificationRequired = (lastVerified: Date | null) =>
    !lastVerified || differenceInMonths(new Date(), lastVerified) >= 3;

  const showVerificationMessage = isVerificationRequired(lastVerified);

  useEffect(() => {
    if (!tab && (hasCprAdminRole || hasManagementRole || hasMarketPartyRole)) {
      if (hasMarketPartyRole) {
        navigate(`/beheer/${OrganisationTabs.MarketPartyRegistration}`, {
          replace: true,
        });
      } else if (hasManagementRole) {
        navigate(`/beheer/${OrganisationTabs.UserManagement}`, {
          replace: true,
        });
      } else if (hasCprAdminRole) {
        navigate(`/beheer/${OrganisationTabs.ContactGroupManagement}`, {
          replace: true,
        });
      }
    }
  }, [hasCprAdminRole, hasManagementRole, hasMarketPartyRole]);

  return (
    <>
      <PageHead title={tom('title')} />
      <Page>
        <PageHeader title={tom('title')}>
          {tab === OrganisationTabs.MarketPartyRegistration && (
            <Button
              as={Link}
              to={thl('edit.market_party_registration')}
              action="secondary"
              emphasis="bold"
              target="_blank"
              rel="noopener noreffer"
            >
              {tmp('button')}
            </Button>
          )}
          {tab === OrganisationTabs.UserManagement && (
            <>
              <UserManagementExport />
              <Button
                as={Link}
                iconStart={<Icon icon={AddIcon} />}
                to="/beheer/gebruikersbeheer/nieuw"
                action="secondary"
                emphasis="bold"
              >
                {tom('header.button.new_user')}
              </Button>
            </>
          )}
          {tab === OrganisationTabs.TeamManagement && <CreateTeamForm />}
          {tab === OrganisationTabs.ContactGroupManagement && (
            <VerifyDataButton />
          )}
          {tab === OrganisationTabs.ContactPersonManagement && (
            <CreateContactModal />
          )}
        </PageHeader>
        <section className="flex items-start gap-6">
          <Tabs
            asChild
            value={tab}
            onValueChange={(val) => navigate(`../${val}`)}
          >
            <Stack gap="lg" className="w-full">
              <TabsList>
                {(hasMarketPartyRole || hasManagementRole) && (
                  <>
                    <TabsTrigger
                      value={OrganisationTabs.MarketPartyRegistration}
                    >
                      {tmp('title')}
                    </TabsTrigger>
                    <TabsTrigger
                      value={OrganisationTabs.PreferredPartyRegistration}
                    >
                      {t('preferred_parties_title')}
                    </TabsTrigger>
                  </>
                )}
                {hasManagementRole && (
                  <>
                    <TabsTrigger value={OrganisationTabs.UserManagement}>
                      {tom('tab.user_management')}
                    </TabsTrigger>
                    <TabsTrigger value={OrganisationTabs.TeamManagement}>
                      {t('common.teams')}
                    </TabsTrigger>
                    <TabsTrigger value={OrganisationTabs.Notifications}>
                      {tom('tab.notifications')}
                    </TabsTrigger>
                  </>
                )}
                {hasCprAdminRole && (
                  <>
                    <TabsTrigger
                      value={OrganisationTabs.ContactGroupManagement}
                    >
                      <div className="flex items-center justify-between gap-2 relative">
                        {t('common.tab.contact_group')}
                        {showVerificationMessage && !isLoading && (
                          <div
                            className="bg-content-error h-2 w-2 rounded-full"
                            data-testid="quality-check-dot"
                          ></div>
                        )}
                      </div>
                    </TabsTrigger>
                    <TabsTrigger
                      value={OrganisationTabs.ContactPersonManagement}
                    >
                      {t('common.tab.contact')}
                    </TabsTrigger>
                  </>
                )}
              </TabsList>
              {(hasMarketPartyRole || hasManagementRole) && (
                <>
                  <TabsContent value={OrganisationTabs.MarketPartyRegistration}>
                    <MarketPartyRegistration />
                  </TabsContent>
                  <TabsContent
                    value={OrganisationTabs.PreferredPartyRegistration}
                  >
                    <PreferredPartyAdministration />
                  </TabsContent>
                </>
              )}
              {hasManagementRole && (
                <>
                  <TabsContent value={OrganisationTabs.UserManagement}>
                    <UserManagement />
                  </TabsContent>
                  <TabsContent value={OrganisationTabs.TeamManagement}>
                    <TeamManagement />
                  </TabsContent>
                  <TabsContent value={OrganisationTabs.Notifications}>
                    <Notifications />
                  </TabsContent>
                </>
              )}
              {hasCprAdminRole && (
                <>
                  <TabsContent value={OrganisationTabs.ContactGroupManagement}>
                    <ContactGroupManagement
                      showVerificationMessage={showVerificationMessage}
                      lastVerified={lastVerified}
                    />
                  </TabsContent>
                  <TabsContent value={OrganisationTabs.ContactPersonManagement}>
                    <ContactPersonManagement />
                  </TabsContent>
                </>
              )}
            </Stack>
          </Tabs>
        </section>
      </Page>
    </>
  );
};
